(() => {
  const ROOT_SELECTOR = '[data-ug-collection-new]';
  const CLIENT_SEARCH_PARAM = 'ug_search';
  const CLIENT_PRICE_MIN_PARAM = 'ug_price_min';
  const CLIENT_PRICE_MAX_PARAM = 'ug_price_max';
  const CLIENT_FILTER_PREFIX = 'ug_filter.';
  const NATIVE_FILTER_PARAM_FRAGMENTS = [
    'custom.fliter_vlaue',
  ];
  const LEGACY_PRICE_MIN_PARAMS = ['filter.v.price.gte'];
  const LEGACY_PRICE_MAX_PARAMS = ['filter.v.price.lte'];
  const FILTER_FORM_IDS = new Set([
    'FacetFiltersForm',
    'FacetFiltersFormMobile',
    'FacetSortForm',
  ]);

  const debounce = (callback, wait) => {
    let timeoutId;

    const fn = (...args) => {
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => callback(...args), wait);
    };

    fn.cancel = () => {
      window.clearTimeout(timeoutId);
    };

    return fn;
  };

  const isNativeFilterParam = (name) =>
    NATIVE_FILTER_PARAM_FRAGMENTS.some((fragment) =>
      String(name || '').includes(fragment)
    );

  class UgMainCollectionNew {
    constructor(root) {
      this.root = root;
      this.gridContainer = root.querySelector('#ProductGridContainer');
      this.gridContent = this.gridContainer?.querySelector(
        '[data-collection-grid-content]'
      );
      this.sectionId =
        root.dataset.sectionId ||
        root.querySelector('#product-grid')?.dataset.id;
      this.cache = new Map();
      this.productPoolCache = new Map();
      this.activePoolController = null;
      this.activePoolKey = '';
      this.abortController = null;
      this.requestSequence = 0;

      this.handleInput = this.handleInput.bind(this);
      this.handleChange = this.handleChange.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.handlePopState = this.handlePopState.bind(this);
      this.handleSortKeydown = this.handleSortKeydown.bind(this);
      this.handleBreakpointChange = this.handleBreakpointChange.bind(this);
      this.handleResize = debounce(() => this.equalizeCardRowHeights(), 150);
      this.submitDebounced = debounce(
        (form, options) => this.submitForm(form, options),
        500
      );
      this.submitSearchDebounced = debounce(() => this.submitClientSearch(), 300);

      this.root.addEventListener('input', this.handleInput);
      this.root.addEventListener('change', this.handleChange);
      this.root.addEventListener('click', this.handleClick);
      window.addEventListener('popstate', this.handlePopState);
      window.addEventListener('resize', this.handleResize);
      this.breakpointMql = window.matchMedia('(min-width: 1024px)');
      if (this.breakpointMql.addEventListener) {
        this.breakpointMql.addEventListener('change', this.handleBreakpointChange);
      } else if (this.breakpointMql.addListener) {
        this.breakpointMql.addListener(this.handleBreakpointChange);
      }

      this.initializePriceRanges();
      this.initializeFilterAccordions();
      this.initializeSortControls();

      // 仅在有实际价格/搜索/客户端筛选项、或 PC/MB 分页不一致时预取完整结果池。
      // 仅存在 filter group 配置时不预取，避免首屏拉取全部分页。
      const migratedParams = this.migrateLegacyClientParams(
        window.location.search.slice(1)
      );
      const urlPrice = this.capturePriceStateFromParams(migratedParams);
      const urlSearch = this.getClientSearchQuery(migratedParams);
      const searchInput = this.root.querySelector('[data-collection-search]');
      if (searchInput && urlSearch) {
        searchInput.value = urlSearch;
      }
      if (urlPrice) {
        this.restorePriceState(urlPrice);
      }

      if (
        this.needsClientFilterPool(migratedParams, {
          priceState: urlPrice,
          searchQuery: urlSearch,
        }) ||
        this.root.hasAttribute('data-client-filter-pending')
      ) {
        if (migratedParams !== window.location.search.slice(1)) {
          window.history.replaceState(
            { searchParams: migratedParams },
            '',
            this.createPageUrl(migratedParams)
          );
        }
        this.render(migratedParams, {
          updateHistory: false,
          updateFilters: true,
        });
        return;
      }

      this.syncSearchClearButton();
      this.setLoading(false);
      this.scheduleCardRowHeights();
      if (document.fonts?.ready) {
        document.fonts.ready.then(() => this.equalizeCardRowHeights());
      }
    }

    destroy() {
      this.abortController?.abort();
      this.activePoolController?.abort();
      this.submitDebounced.cancel();
      this.submitSearchDebounced.cancel();
      this.handleResize.cancel?.();
      if (this.breakpointMql?.removeEventListener) {
        this.breakpointMql.removeEventListener(
          'change',
          this.handleBreakpointChange
        );
      } else if (this.breakpointMql?.removeListener) {
        this.breakpointMql.removeListener(this.handleBreakpointChange);
      }
      this.root.removeEventListener('input', this.handleInput);
      this.root.removeEventListener('change', this.handleChange);
      this.root.removeEventListener('click', this.handleClick);
      window.removeEventListener('popstate', this.handlePopState);
      window.removeEventListener('resize', this.handleResize);
      this.filterAccordionAbortController?.abort();
      this.sortControlsAbortController?.abort();
      window.clearTimeout(this.filterDrawerSettledTimeout);
      this.closeSortMenu();
      this.closeSortSheet({ restoreFocus: false });
      this.closeFilterDrawer({ revert: false, restoreFocus: false });
    }

    handleInput(event) {
      if (event.target.matches('[data-collection-search]')) {
        this.syncSearchClearButton();
        this.submitSearchDebounced();
        return;
      }

      const form = event.target.closest('form');
      if (!form || !FILTER_FORM_IDS.has(form.id)) return;

      if (
        event.target.matches('[data-single-select-checkbox]') &&
        event.target.checked
      ) {
        event.target
          .closest('[data-single-select-group]')
          ?.querySelectorAll('[data-single-select-checkbox]')
          .forEach((checkbox) => {
            if (checkbox !== event.target) {
              checkbox.checked = false;
            }
          });
      }

      const priceRange = event.target.closest('[data-ug-price-range-v2]');
      if (priceRange && event.target.matches('input')) {
        this.updatePriceRange(priceRange, event.target);
        if (form.id === 'FacetFiltersFormMobile') {
          this.updateMobileDraftFacetCounts(form);
          return;
        }
        this.syncPriceRanges(priceRange);
        this.applyClientFilters({ includePrice: true });

        // 滑块只在 change（松手）时提交，避免拖动中连发 + 与 change 双请求
        if (
          event.target.matches(
            '[data-price-min-slider], [data-price-max-slider]'
          )
        ) {
          return;
        }
      }

      if (form.id === 'FacetFiltersFormMobile') {
        this.updateMobileDraftFacetCounts(form);
        return;
      }

      this.submitDebounced(form, {
        focusId: event.target.closest('.js-filter')?.id,
      });
    }

    handleChange(event) {
      const form = event.target.closest('form');
      if (!form || !FILTER_FORM_IDS.has(form.id)) return;

      const priceRange = event.target.closest('[data-ug-price-range-v2]');
      if (
        priceRange &&
        event.target.matches('[data-price-min-slider], [data-price-max-slider]')
      ) {
        this.updatePriceRange(priceRange, event.target);
        if (form.id === 'FacetFiltersFormMobile') {
          this.updateMobileDraftFacetCounts(form);
          return;
        }
        this.syncPriceRanges(priceRange);
        this.applyClientFilters({ includePrice: true });
        this.submitDebounced.cancel();
        this.submitForm(form, {
          focusId: priceRange.closest('.js-filter')?.id,
        });
      }
    }

    handleSortKeydown(event) {
      if (event.key === 'Escape') {
        this.closeSortMenu();
        this.closeSortSheet();
        this.closeFilterDrawer();
        return;
      }

      if (event.key !== 'Tab') return;
      const activeLayer =
        this.root.querySelector('[data-collection-filter-drawer].is-open') ||
        this.root.querySelector('[data-collection-sort-sheet].is-open');
      if (activeLayer) this.trapFocus(event, activeLayer);
    }

    getFocusableElements(container) {
      if (!container) return [];
      return [
        ...container.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ),
      ].filter(
        (element) =>
          !element.hidden &&
          element.getAttribute('aria-hidden') !== 'true' &&
          element.getClientRects().length > 0
      );
    }

    trapFocus(event, container) {
      const focusable = this.getFocusableElements(container);
      if (focusable.length === 0) {
        event.preventDefault();
        container.focus?.();
        return;
      }

      const first = focusable[0];
      const last = focusable.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    handleClick(event) {
      const clearSearchButton = event.target.closest(
        '[data-collection-search-clear]'
      );
      if (clearSearchButton) {
        event.preventDefault();
        this.clearClientSearch();
        return;
      }

      const filterOpenButton = event.target.closest(
        '[data-collection-filter-open]'
      );
      if (filterOpenButton && this.root.contains(filterOpenButton)) {
        event.preventDefault();
        this.openFilterDrawer();
        return;
      }

      const filterResetButton = event.target.closest(
        '[data-collection-filter-reset]'
      );
      if (filterResetButton && this.root.contains(filterResetButton)) {
        event.preventDefault();
        this.resetMobileFilterDraft();
        return;
      }

      const filterApplyButton = event.target.closest(
        '[data-collection-filter-apply]'
      );
      if (filterApplyButton && this.root.contains(filterApplyButton)) {
        event.preventDefault();
        const form = this.root.querySelector('#FacetFiltersFormMobile');
        if (form) {
          const mobilePriceRange = form.querySelector(
            '[data-ug-price-range-v2]'
          );
          if (mobilePriceRange) {
            this.updatePriceRange(mobilePriceRange);
            this.syncPriceRanges(mobilePriceRange);
          }
          this.submitDebounced.cancel();
          this.submitForm(form);
        }
        this.closeFilterDrawer({ revert: false });
        return;
      }

      const filterCloseButton = event.target.closest(
        '[data-collection-filter-close]'
      );
      if (filterCloseButton && this.root.contains(filterCloseButton)) {
        event.preventDefault();
        this.closeFilterDrawer();
        return;
      }

      const sortTrigger = event.target.closest('[data-collection-sort-trigger]');
      if (
        sortTrigger &&
        this.root.contains(sortTrigger) &&
        window.matchMedia('(min-width: 1024px)').matches
      ) {
        event.preventDefault();
        const dropdown = sortTrigger.closest('.ug-collection-new__sort-dropdown');
        const menu = dropdown?.querySelector('[data-collection-sort-menu]');
        const isOpen = dropdown?.classList.contains('is-open');

        this.closeSortSheet();

        if (isOpen) {
          this.closeSortMenu();
          return;
        }

        this.closeSortMenu();
        dropdown?.classList.add('is-open');
        sortTrigger.setAttribute('aria-expanded', 'true');
        menu?.classList.add('is-open');
        return;
      }

      const sortMobileOpen = event.target.closest(
        '[data-collection-sort-mobile-open]'
      );
      if (
        sortMobileOpen &&
        this.root.contains(sortMobileOpen) &&
        window.matchMedia('(max-width: 1023.98px)').matches
      ) {
        event.preventDefault();
        this.openSortSheet(sortMobileOpen);
        return;
      }

      const sortClose = event.target.closest('[data-collection-sort-close]');
      if (sortClose && this.root.contains(sortClose)) {
        event.preventDefault();
        this.closeSortSheet();
        return;
      }

      const sortOption = event.target.closest('[data-sort-option]');
      if (sortOption && this.root.contains(sortOption)) {
        event.preventDefault();
        const isDesktopOption = Boolean(
          sortOption.closest('[data-collection-sort-menu]')
        );
        const isMobileOption = Boolean(
          sortOption.closest('[data-collection-sort-sheet]')
        );
        const isDesktop = window.matchMedia('(min-width: 1024px)').matches;

        if ((isDesktop && isDesktopOption) || (!isDesktop && isMobileOption)) {
          this.applySortValue(
            sortOption.dataset.sortValue,
            sortOption.textContent
          );
        }
        return;
      }

      if (
        !event.target.closest('[data-collection-sort-desktop]') &&
        !event.target.closest('[data-collection-sort-menu]')
      ) {
        this.closeSortMenu();
      }

      const rangeButton = event.target.closest('[data-price-range-button]');
      if (rangeButton && this.root.contains(rangeButton)) {
        event.preventDefault();
        const priceRange = rangeButton.closest('[data-ug-price-range-v2]');
        const form = rangeButton.closest('form');
        if (!priceRange || !form) return;

        const minInput = priceRange.querySelector('[data-price-min-input]');
        const maxInput = priceRange.querySelector('[data-price-max-input]');
        const minSlider = priceRange.querySelector('[data-price-min-slider]');
        const maxSlider = priceRange.querySelector('[data-price-max-slider]');
        const rangeMin = rangeButton.dataset.rangeMin || '';
        const rangeMax =
          rangeButton.dataset.rangeMax ||
          priceRange.dataset.sliderMax ||
          maxSlider?.max ||
          '';

        if (minInput) minInput.value = rangeMin;
        if (maxInput) maxInput.value = rangeMax;
        if (minSlider) minSlider.value = rangeMin || minSlider.min;
        if (maxSlider) maxSlider.value = rangeMax || maxSlider.max;

        this.updatePriceRange(priceRange);
        if (form.id === 'FacetFiltersFormMobile') {
          this.updateMobileDraftFacetCounts(form);
          return;
        }
        this.syncPriceRanges(priceRange);
        this.applyClientFilters({ includePrice: true });
        this.submitDebounced.cancel();
        this.submitForm(form, {
          focusId: priceRange.closest('.js-filter')?.id,
        });
        return;
      }

      const removeLink = event.target.closest('facet-remove a[href]');
      if (removeLink && this.root.contains(removeLink)) {
        event.preventDefault();
        const url = new URL(removeLink.href, window.location.origin);
        this.render(url.searchParams.toString(), {
          updateHistory: true,
          updateFilters: true,
          focusId: removeLink.closest('.js-filter')?.id,
        });
        return;
      }

      const paginationLink = event.target.closest(
        '[data-collection-pagination] a[href]'
      );
      if (paginationLink && this.root.contains(paginationLink)) {
        event.preventDefault();
        const url = new URL(paginationLink.href, window.location.origin);
        this.render(url.searchParams.toString(), {
          updateHistory: true,
          updateFilters: true,
          scrollToSection: true,
        });
      }
    }

    handlePopState() {
      const params = this.migrateLegacyClientParams(
        window.location.search.slice(1)
      );
      const urlPrice = this.capturePriceStateFromParams(params);
      const urlSearch = this.getClientSearchQuery(params);
      const searchInput = this.root.querySelector('[data-collection-search]');
      if (searchInput) searchInput.value = urlSearch || '';
      if (urlPrice) this.restorePriceState(urlPrice);
      this.syncSearchClearButton();

      this.render(params, {
        updateHistory: false,
        updateFilters: true,
      });
    }

    submitForm(sourceForm, { focusId = null } = {}) {
      const resolvedFocusId =
        focusId ||
        sourceForm?.querySelector?.('.js-filter[open]')?.id ||
        null;

      this.syncFilterForms(sourceForm);
      this.render(this.createSearchParams(sourceForm), {
        updateHistory: true,
        updateFilters: true,
        focusId: resolvedFocusId,
      });
    }

    syncFilterForms(sourceForm) {
      if (!sourceForm) return;

      const targetForm =
        sourceForm.id === 'FacetFiltersFormMobile'
          ? this.root.querySelector('#FacetFiltersForm')
          : this.root.querySelector('#FacetFiltersFormMobile');
      if (!targetForm || targetForm === sourceForm) {
        const sourcePrice = sourceForm.querySelector('[data-ug-price-range-v2]');
        if (sourcePrice) this.syncPriceRanges(sourcePrice);
        return;
      }

      targetForm.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
        checkbox.checked = false;
      });

      sourceForm
        .querySelectorAll('input[type="checkbox"]:checked')
        .forEach((checkbox) => {
          const group = checkbox.dataset.filterGroup;
          const option = checkbox.dataset.filterOption;
          if (!group || !option) return;
          const selector = `input[type="checkbox"][data-filter-group="${CSS.escape(
            group
          )}"][data-filter-option="${CSS.escape(option)}"]`;
          const match = targetForm.querySelector(selector);
          if (match) match.checked = true;
        });

      const sourcePrice = sourceForm.querySelector('[data-ug-price-range-v2]');
      if (sourcePrice) this.syncPriceRanges(sourcePrice);
    }

    createSearchParams(sourceForm) {
      const params = new URLSearchParams();
      const sourceIsMobile = sourceForm.id === 'FacetFiltersFormMobile';
      const filterForm = this.root.querySelector(
        sourceIsMobile
          ? '#FacetFiltersFormMobile'
          : '#FacetFiltersForm'
      );
      const sortForm = this.root.querySelector('#FacetSortForm');

      [filterForm, sortForm].forEach((form) => {
        if (!form) return;

        new FormData(form).forEach((value, key) => {
          if (value === '' || value == null) return;
          // 价格只走 ug_price_*，避免把 Shopify 原生价格参数写进 URL。
          if (
            key === CLIENT_PRICE_MIN_PARAM ||
            key === CLIENT_PRICE_MAX_PARAM ||
            LEGACY_PRICE_MIN_PARAMS.includes(key) ||
            LEGACY_PRICE_MAX_PARAMS.includes(key)
          ) {
            return;
          }
          if (isNativeFilterParam(key)) return;
          params.append(key, value);
        });
      });

      filterForm
        ?.querySelectorAll(
          '.ug-cf-checkbox__input[data-filter-group][data-filter-option]:checked'
        )
        .forEach((input) => {
          params.append(
            `${CLIENT_FILTER_PREFIX}${input.dataset.filterGroup}`,
            input.dataset.filterOption
          );
        });

      this.applyPriceParams(params, filterForm);
      this.stripLegacyPriceParams(params);

      const searchQuery = (
        this.root.querySelector('[data-collection-search]')?.value || ''
      ).trim();
      if (searchQuery) {
        params.set(CLIENT_SEARCH_PARAM, searchQuery);
      } else {
        params.delete(CLIENT_SEARCH_PARAM);
      }

      params.delete('page');
      return params.toString();
    }

    stripLegacyPriceParams(params) {
      LEGACY_PRICE_MIN_PARAMS.forEach((name) => params.delete(name));
      LEGACY_PRICE_MAX_PARAMS.forEach((name) => params.delete(name));
    }

    migrateLegacyClientParams(searchParams) {
      const original = searchParams || '';
      const params = new URLSearchParams(original);
      let changed = false;

      LEGACY_PRICE_MIN_PARAMS.forEach((name) => {
        if (!params.has(name)) return;
        if (!params.has(CLIENT_PRICE_MIN_PARAM)) {
          params.set(CLIENT_PRICE_MIN_PARAM, params.get(name));
        }
        params.delete(name);
        changed = true;
      });

      LEGACY_PRICE_MAX_PARAMS.forEach((name) => {
        if (!params.has(name)) return;
        if (!params.has(CLIENT_PRICE_MAX_PARAM)) {
          params.set(CLIENT_PRICE_MAX_PARAM, params.get(name));
        }
        params.delete(name);
        changed = true;
      });

      [...params.entries()].forEach(([name, value]) => {
        if (!isNativeFilterParam(name)) return;

        const matchingInput = [
          ...this.root.querySelectorAll(
            '#FacetFiltersForm [data-filter-group][data-native-value]'
          ),
        ].find((input) => input.dataset.nativeValue === value);

        if (matchingInput?.dataset.filterGroup) {
          params.append(
            `${CLIENT_FILTER_PREFIX}${matchingInput.dataset.filterGroup}`,
            matchingInput.dataset.filterOption || value
          );
        }
        params.delete(name);
        changed = true;
      });

      return changed ? params.toString() : original;
    }

    hasClientFacetParams(searchParams = '') {
      return [...new URLSearchParams(searchParams).keys()].some((name) =>
        name.startsWith(CLIENT_FILTER_PREFIX)
      );
    }

    /**
     * 是否需要拉取完整产品池做客户端筛选/重分页。
     * 仅配置了筛选项、但当前无激活条件时返回 false，走普通 section 请求。
     */
    needsClientFilterPool(
      searchParams = '',
      { priceState = null, searchQuery = null } = {}
    ) {
      const resolvedPrice =
        priceState === null
          ? this.capturePriceStateFromParams(searchParams)
          : priceState;
      const resolvedSearch =
        searchQuery === null
          ? this.getClientSearchQuery(searchParams)
          : searchQuery;

      return (
        Boolean(resolvedPrice) ||
        Boolean(resolvedSearch) ||
        this.hasClientFacetParams(searchParams) ||
        this.hasSplitPageSize()
      );
    }

    getClientSearchQuery(searchParams = null) {
      if (searchParams != null) {
        const params = new URLSearchParams(searchParams || '');
        return (params.get(CLIENT_SEARCH_PARAM) || '').trim();
      }
      return (
        this.root.querySelector('[data-collection-search]')?.value || ''
      ).trim();
    }

    syncSearchClearButton() {
      const input = this.root.querySelector('[data-collection-search]');
      const clearButton = this.root.querySelector(
        '[data-collection-search-clear]'
      );
      if (!clearButton) return;

      const query = (input?.value || '').trim();
      clearButton.classList.toggle('hidden', !query);
      clearButton.classList.toggle('flex', Boolean(query));
    }

    submitClientSearch() {
      const filterForm =
        this.root.querySelector('#FacetFiltersForm') ||
        this.root.querySelector('#FacetFiltersFormMobile');
      if (filterForm) this.syncFilterForms(filterForm);

      const params = new URLSearchParams(
        filterForm
          ? this.createSearchParams(filterForm)
          : this.migrateLegacyClientParams(window.location.search.slice(1))
      );
      const query = (
        this.root.querySelector('[data-collection-search]')?.value || ''
      ).trim();

      if (query) {
        params.set(CLIENT_SEARCH_PARAM, query);
      } else {
        params.delete(CLIENT_SEARCH_PARAM);
      }
      this.stripLegacyPriceParams(params);
      params.delete('page');

      this.render(params.toString(), {
        updateHistory: true,
        updateFilters: true,
      });
    }

    getPriceBoundValues(priceRange) {
      const minInput = priceRange.querySelector('[data-price-min-input]');
      const maxInput = priceRange.querySelector('[data-price-max-input]');
      const minSlider = priceRange.querySelector('[data-price-min-slider]');
      const maxSlider = priceRange.querySelector('[data-price-max-slider]');
      const sliderMin = Math.round(
        Number(
          priceRange.dataset.sliderMin ||
            minSlider?.min ||
            minInput?.min ||
            0
        )
      );
      const sliderMax = Math.round(
        Number(
          priceRange.dataset.sliderMax ||
            maxSlider?.max ||
            maxInput?.max ||
            0
        )
      );

      let minValue = Math.round(
        Number(minSlider?.value ?? minInput?.value ?? sliderMin)
      );
      let maxValue = Math.round(
        Number(maxSlider?.value ?? maxInput?.value ?? sliderMax)
      );

      if (Number.isNaN(minValue)) minValue = sliderMin;
      if (Number.isNaN(maxValue)) maxValue = sliderMax;

      minValue = Math.min(Math.max(minValue, sliderMin), sliderMax);
      maxValue = Math.min(Math.max(maxValue, sliderMin), sliderMax);
      if (minValue > maxValue) maxValue = minValue;

      return {
        minInput,
        maxInput,
        minSlider,
        maxSlider,
        sliderMin,
        sliderMax,
        minValue,
        maxValue,
      };
    }

    applyPriceParams(params, filterForm) {
      if (!filterForm) return;

      const priceRange = filterForm.querySelector('[data-ug-price-range-v2]');
      if (!priceRange) return;

      const {
        minInput,
        maxInput,
        minSlider,
        maxSlider,
        sliderMin,
        sliderMax,
        minValue,
        maxValue,
      } = this.getPriceBoundValues(priceRange);

      if (minSlider) minSlider.value = String(minValue);
      if (maxSlider) maxSlider.value = String(maxValue);
      if (minInput) minInput.value = String(minValue);
      if (maxInput) maxInput.value = String(maxValue);

      // 满区间不提交价格参数；始终使用 ug_price_*，不写 Shopify 原生价格参数。
      params.delete(CLIENT_PRICE_MIN_PARAM);
      params.delete(CLIENT_PRICE_MAX_PARAM);
      this.stripLegacyPriceParams(params);
      if (minInput?.name) params.delete(minInput.name);
      if (maxInput?.name) params.delete(maxInput.name);

      const isFullRange = minValue <= sliderMin && maxValue >= sliderMax;
      if (!isFullRange) {
        params.set(CLIENT_PRICE_MIN_PARAM, String(minValue));
        params.set(CLIENT_PRICE_MAX_PARAM, String(maxValue));
        if (minInput) minInput.value = String(minValue);
        if (maxInput) maxInput.value = String(maxValue);
      }
    }

    syncPriceRanges(sourcePriceRange) {
      if (!sourcePriceRange) return;

      const { minValue, maxValue } = this.getPriceBoundValues(sourcePriceRange);
      this.restorePriceState({ minValue, maxValue }, sourcePriceRange);
    }

    capturePriceStateFromParams(searchParams) {
      const priceRange = this.root.querySelector('[data-ug-price-range-v2]');
      if (!priceRange) return null;

      const minInput = priceRange.querySelector('[data-price-min-input]');
      const maxInput = priceRange.querySelector('[data-price-max-input]');
      const params = new URLSearchParams(searchParams || '');

      const minRaw =
        params.get(CLIENT_PRICE_MIN_PARAM) ??
        LEGACY_PRICE_MIN_PARAMS.map((name) => params.get(name)).find(
          (value) => value != null && value !== ''
        ) ??
        (minInput?.name ? params.get(minInput.name) : null);
      const maxRaw =
        params.get(CLIENT_PRICE_MAX_PARAM) ??
        LEGACY_PRICE_MAX_PARAMS.map((name) => params.get(name)).find(
          (value) => value != null && value !== ''
        ) ??
        (maxInput?.name ? params.get(maxInput.name) : null);

      if (minRaw == null && maxRaw == null) return null;

      const sliderMin = Math.round(
        Number(priceRange.dataset.sliderMin || minInput?.min || 0)
      );
      const sliderMax = Math.round(
        Number(priceRange.dataset.sliderMax || maxInput?.max || 0)
      );
      let minValue =
        minRaw != null ? Math.round(Number(minRaw)) : sliderMin;
      let maxValue =
        maxRaw != null ? Math.round(Number(maxRaw)) : sliderMax;

      if (Number.isNaN(minValue)) minValue = sliderMin;
      if (Number.isNaN(maxValue)) maxValue = sliderMax;

      minValue = Math.min(Math.max(minValue, sliderMin), sliderMax);
      maxValue = Math.min(Math.max(maxValue, sliderMin), sliderMax);
      if (minValue > maxValue) maxValue = minValue;

      return { minValue, maxValue, sliderMin, sliderMax };
    }

    restorePriceState(state, exceptPriceRange = null) {
      if (!state) return;

      this.root.querySelectorAll('[data-ug-price-range-v2]').forEach((priceRange) => {
        if (exceptPriceRange && priceRange === exceptPriceRange) {
          this.updateDualPriceFill(priceRange);
          this.updatePriceLabels(priceRange);
          this.syncActivePriceRangeButton(priceRange);
          return;
        }

        const minInput = priceRange.querySelector('[data-price-min-input]');
        const maxInput = priceRange.querySelector('[data-price-max-input]');
        const minSlider = priceRange.querySelector('[data-price-min-slider]');
        const maxSlider = priceRange.querySelector('[data-price-max-slider]');

        if (minInput) minInput.value = String(state.minValue);
        if (maxInput) maxInput.value = String(state.maxValue);
        if (minSlider) minSlider.value = String(state.minValue);
        if (maxSlider) maxSlider.value = String(state.maxValue);

        this.updateDualPriceFill(priceRange);
        this.updatePriceLabels(priceRange);
        this.syncActivePriceRangeButton(priceRange);
      });
    }

    getPriceParamNames() {
      return [
        CLIENT_PRICE_MIN_PARAM,
        CLIENT_PRICE_MAX_PARAM,
        ...LEGACY_PRICE_MIN_PARAMS,
        ...LEGACY_PRICE_MAX_PARAMS,
      ];
    }

    createProductPoolParams(searchParams) {
      const params = new URLSearchParams(searchParams || '');
      this.getPriceParamNames().forEach((name) => params.delete(name));
      params.delete(CLIENT_SEARCH_PARAM);
      params.delete('page');
      [...params.keys()].forEach((name) => {
        if (
          name.startsWith(CLIENT_FILTER_PREFIX) ||
          isNativeFilterParam(name)
        ) {
          params.delete(name);
        }
      });
      return params;
    }

    normalizeFilterValue(value) {
      return String(value || '')
        .normalize('NFKC')
        .trim()
        .replace(/\s+/g, ' ')
        .toLocaleLowerCase();
    }

    getFilterGroups(scope = this.root) {
      const groups = new Map();
      scope
        .querySelectorAll(
          '#FacetFiltersForm .ug-cf-checkbox__input[data-filter-group][data-filter-option]'
        )
        .forEach((input) => {
          const key = input.dataset.filterGroup;
          const optionKey = (input.dataset.filterOption || '').trim();
          const label = (input.dataset.filterLabel || optionKey).trim();
          if (!key || !optionKey) return;

          if (!groups.has(key)) {
            groups.set(key, {
              key,
              multiple: input.dataset.filterMultiple === 'true',
              options: new Map(),
            });
          }

          groups.get(key).options.set(this.normalizeFilterValue(optionKey), {
            label,
            normalized: this.normalizeFilterValue(optionKey),
            normalizedLabel: this.normalizeFilterValue(label),
          });
        });
      return groups;
    }

    createProductRecord(card, groups) {
      const rawValues = new Set(
        String(card.dataset.filterValues || '')
          .split('||')
          .map((value) => this.normalizeFilterValue(value))
          .filter(Boolean)
      );
      const memberships = new Map();

      groups.forEach((group) => {
        memberships.set(group.key, new Set());
      });

      if (
        card.dataset.filterMembershipsReady === 'true' &&
        String(card.dataset.filterMemberships || '').trim()
      ) {
        String(card.dataset.filterMemberships || '')
          .split('||')
          .filter(Boolean)
          .forEach((membership) => {
            const separatorIndex = membership.indexOf('::');
            if (separatorIndex < 1) return;
            const groupKey = membership.slice(0, separatorIndex);
            let optionLabel = membership.slice(separatorIndex + 2);
            try {
              optionLabel = decodeURIComponent(optionLabel);
            } catch (_error) {
              // Keep the original label if malformed legacy data is encountered.
            }
            const normalized = this.normalizeFilterValue(optionLabel);
            const group = groups.get(groupKey);
            if (group?.options.has(normalized)) {
              memberships.get(groupKey).add(normalized);
            }
          });
      }

      groups.forEach((group) => {
        const groupMemberships = memberships.get(group.key);
        if (!groupMemberships || groupMemberships.size > 0) return;

        group.options.forEach((option) => {
          if (
            rawValues.has(option.normalized) ||
            rawValues.has(option.normalizedLabel)
          ) {
            groupMemberships.add(option.normalized);
          }
        });
      });

      return {
        key:
          card.dataset.productKey ||
          card.querySelector('a')?.getAttribute('href') ||
          card.innerHTML,
        title: (card.dataset.productTitle || '').toLocaleLowerCase(),
        priceCents: Number(card.dataset.priceCents),
        memberships,
        card,
      };
    }

    getActiveFacetSelections(searchParams, groups) {
      const params = new URLSearchParams(searchParams || '');
      const selections = new Map();

      groups.forEach((group) => {
        const values = params
          .getAll(`${CLIENT_FILTER_PREFIX}${group.key}`)
          .map((value) => {
            const normalized = this.normalizeFilterValue(value);
            if (group.options.has(normalized)) return normalized;
            return [...group.options.values()].find(
              (option) =>
                this.normalizeFilterValue(option.label) === normalized
            )?.normalized;
          })
          .filter(Boolean);
        if (values.length > 0) {
          selections.set(group.key, group.multiple ? values : [values.at(-1)]);
        }
      });
      return selections;
    }

    updateMobileDraftFacetCounts(
      mobileForm = this.root.querySelector('#FacetFiltersFormMobile')
    ) {
      const poolIndex = this.currentPoolIndex;
      if (!mobileForm || !poolIndex?.records || !poolIndex?.groups) return;

      const draftParams = this.createSearchParams(mobileForm);
      const priceState = this.capturePriceStateFromParams(draftParams);
      const searchQuery = this.getClientSearchQuery(draftParams)
        .trim()
        .toLocaleLowerCase();
      const minCents = priceState ? priceState.minValue * 100 : null;
      const maxCents = priceState ? priceState.maxValue * 100 : null;
      const selections = this.getActiveFacetSelections(
        draftParams,
        poolIndex.groups
      );
      const priceSearchRecords = poolIndex.records.filter((record) => {
        if (
          priceState &&
          (!Number.isFinite(record.priceCents) ||
            record.priceCents < minCents ||
            record.priceCents > maxCents)
        ) {
          return false;
        }
        return !searchQuery || record.title.includes(searchQuery);
      });

      this.syncFacetAvailability(
        priceSearchRecords,
        mobileForm,
        poolIndex.groups,
        selections
      );
    }

    resetMobileFilterDraft() {
      const mobileForm = this.root.querySelector('#FacetFiltersFormMobile');
      if (!mobileForm) return;

      mobileForm
        .querySelectorAll(
          '.ug-cf-checkbox__input[data-filter-group][data-filter-option]'
        )
        .forEach((input) => {
          input.checked = false;
        });

      mobileForm.querySelectorAll('[data-ug-price-range-v2]').forEach((range) => {
        const minValue = range.dataset.sliderMin || '0';
        const maxValue = range.dataset.sliderMax || '';
        const minInput = range.querySelector('[data-price-min-input]');
        const maxInput = range.querySelector('[data-price-max-input]');
        const minSlider = range.querySelector('[data-price-min-slider]');
        const maxSlider = range.querySelector('[data-price-max-slider]');

        if (minInput) minInput.value = minValue;
        if (maxInput) maxInput.value = maxValue;
        if (minSlider) minSlider.value = minValue;
        if (maxSlider) maxSlider.value = maxValue || maxSlider.max;
        this.updatePriceRange(range);
      });

      this.updateMobileDraftFacetCounts(mobileForm);
    }

    restoreFacetSelections(scope, selections) {
      scope
        .querySelectorAll(
          '.ug-cf-checkbox__input[data-filter-group][data-filter-option]'
        )
        .forEach((input) => {
          const selected = selections.get(input.dataset.filterGroup) || [];
          input.checked = selected.some(
            (value) =>
              value ===
              this.normalizeFilterValue(input.dataset.filterOption)
          );
        });
    }

    recordMatchesSelections(record, groups, selections, excludedGroup = '') {
      for (const [groupKey, selectedValues] of selections.entries()) {
        if (groupKey === excludedGroup || selectedValues.length === 0) continue;
        const memberships = record.memberships.get(groupKey) || new Set();
        const matches = selectedValues.some((value) => memberships.has(value));
        if (!matches) return false;
      }
      return true;
    }

    getFacetCounts(records, groups, selections) {
      const counts = new Map();

      groups.forEach((group) => {
        const groupCounts = new Map(
          [...group.options.values()].map((option) => [option.normalized, 0])
        );

        records.forEach((record) => {
          if (
            !this.recordMatchesSelections(
              record,
              groups,
              selections,
              group.key
            )
          ) {
            return;
          }

          (record.memberships.get(group.key) || new Set()).forEach((value) => {
            if (groupCounts.has(value)) {
              groupCounts.set(value, groupCounts.get(value) + 1);
            }
          });
        });
        counts.set(group.key, groupCounts);
      });

      return counts;
    }

    syncFacetAvailability(
      records,
      scope = this.root,
      groups = this.getFilterGroups(scope),
      selections = new Map()
    ) {
      const counts = this.getFacetCounts(records, groups, selections);

      scope
        .querySelectorAll(
          '.ug-cf-checkbox__input[data-filter-group][data-filter-option]'
        )
        .forEach((input) => {
          const groupKey = input.dataset.filterGroup;
          const optionKey = this.normalizeFilterValue(
            input.dataset.filterOption
          );
          const count = counts.get(groupKey)?.get(optionKey) || 0;
          const isDisabled = count === 0 && !input.checked;
          input.disabled = isDisabled;

          const option = input.closest('.ug-cf-option');
          option?.classList.toggle('disabled', isDisabled);

          const label = input.closest('label');
          label?.classList.toggle('ug-cf-checkbox--disabled', isDisabled);
          label?.classList.toggle('lg:cursor-not-allowed', isDisabled);
          label?.classList.toggle('lg:cursor-pointer', !isDisabled);
          label?.classList.toggle('cursor-not-allowed', isDisabled);
          label?.classList.toggle('cursor-pointer', !isDisabled);

          const labelText = label?.querySelector('.ug-cf-checkbox__label');
          if (labelText) {
            const base =
              labelText.dataset.labelBase ||
              labelText.textContent.replace(/\s*\(\d+\)\s*$/, '').trim();
            labelText.dataset.labelBase = base;
            labelText.textContent = `${base} (${count})`;
          }
        });
    }

    async fetchSection(searchParams, signal) {
      const requestUrl = this.createSectionUrl(searchParams);
      const cachedHtml = this.cache.get(requestUrl);
      if (cachedHtml) return cachedHtml;

      const response = await fetch(requestUrl, {
        signal,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) {
        throw new Error(
          `Collection request failed with status ${response.status}`
        );
      }

      const html = await response.text();
      this.cache.set(requestUrl, html);
      while (this.cache.size > 300) {
        this.cache.delete(this.cache.keys().next().value);
      }
      return html;
    }

    async loadProductPool(poolParams) {
      const normalizedParams = new URLSearchParams(poolParams || '');
      normalizedParams.delete('page');
      const poolKey = normalizedParams.toString();
      const cachedPool = this.productPoolCache.get(poolKey);
      if (cachedPool) return cachedPool;

      if (this.activePoolKey && this.activePoolKey !== poolKey) {
        this.productPoolCache.delete(this.activePoolKey);
        this.activePoolController?.abort();
      }

      const controller = new AbortController();
      this.activePoolController = controller;
      this.activePoolKey = poolKey;

      const poolPromise = (async () => {
        const firstHtml = await this.fetchSection(
          normalizedParams.toString(),
          controller.signal
        );
        const firstDocument = new DOMParser().parseFromString(
          firstHtml,
          'text/html'
        );
        const totalPages =
          this.getResponseTotalPagesFromDocument(firstDocument);
        const htmlPages = [firstHtml];
        const batchSize = 4;

        this.root.dataset.poolLoadedPages = '1';
        this.root.dataset.poolTotalPages = String(totalPages);

        for (
          let batchStart = 2;
          batchStart <= totalPages;
          batchStart += batchSize
        ) {
          const batchEnd = Math.min(
            batchStart + batchSize - 1,
            totalPages
          );
          const requests = [];

          for (let page = batchStart; page <= batchEnd; page += 1) {
            const pageParams = new URLSearchParams(normalizedParams);
            pageParams.set('page', String(page));
            requests.push(
              this.fetchSection(pageParams.toString(), controller.signal)
            );
          }

          htmlPages.push(...(await Promise.all(requests)));
          this.root.dataset.poolLoadedPages = String(htmlPages.length);
        }

        return this.createProductPoolIndex(htmlPages, { firstDocument });
      })();

      this.productPoolCache.set(poolKey, poolPromise);

      try {
        const poolIndex = await poolPromise;
        this.productPoolCache.set(poolKey, Promise.resolve(poolIndex));

        while (this.productPoolCache.size > 3) {
          const oldestKey = this.productPoolCache.keys().next().value;
          if (oldestKey === poolKey) break;
          this.productPoolCache.delete(oldestKey);
        }
        return poolIndex;
      } catch (error) {
        if (this.productPoolCache.get(poolKey) === poolPromise) {
          this.productPoolCache.delete(poolKey);
        }
        throw error;
      } finally {
        if (this.activePoolController === controller) {
          this.activePoolController = null;
          this.activePoolKey = '';
        }
      }
    }

    getResponseTotalPagesFromDocument(documentFromResponse) {
      const nextRoot =
        documentFromResponse?.getElementById(this.root.id) ||
        documentFromResponse?.querySelector(ROOT_SELECTOR);
      const gridContent = nextRoot?.querySelector(
        '[data-collection-grid-content]'
      );

      return Math.max(1, Number(gridContent?.dataset.totalPages) || 1);
    }

    getResponseTotalPages(html) {
      return this.getResponseTotalPagesFromDocument(
        new DOMParser().parseFromString(html, 'text/html')
      );
    }

    createProductPoolIndex(htmlPages, { firstDocument = null } = {}) {
      const documents = htmlPages.map((html, index) => {
        if (index === 0 && firstDocument) return firstDocument;
        return new DOMParser().parseFromString(html, 'text/html');
      });
      const resolvedFirstDocument = documents[0];
      const firstRoot =
        resolvedFirstDocument.getElementById(this.root.id) ||
        resolvedFirstDocument.querySelector(ROOT_SELECTOR);
      const groups = this.getFilterGroups(firstRoot);
      const seen = new Set();
      const records = [];

      documents.forEach((documentFromResponse) => {
        documentFromResponse
          .querySelectorAll('[data-collection-product]')
          .forEach((card) => {
            const record = this.createProductRecord(card, groups);
            if (seen.has(record.key)) return;
            seen.add(record.key);
            records.push(record);
          });
      });

      return {
        htmlPages,
        documents,
        firstDocument: resolvedFirstDocument,
        firstRoot,
        groups,
        records,
      };
    }

    getPaginationParts(currentPage, totalPages) {
      if (totalPages <= 7) {
        return Array.from({ length: totalPages }, (_, index) => index + 1);
      }

      const pages = [...new Set([
        1,
        currentPage - 1,
        currentPage,
        currentPage + 1,
        totalPages,
      ])]
        .filter((page) => page >= 1 && page <= totalPages)
        .sort((a, b) => a - b);
      const parts = [];

      pages.forEach((page, index) => {
        if (index > 0 && page - pages[index - 1] > 1) {
          parts.push(null);
        }
        parts.push(page);
      });

      return parts;
    }

    createPaginationHref(searchParams, page) {
      const params = new URLSearchParams(searchParams || '');
      if (page <= 1) {
        params.delete('page');
      } else {
        params.set('page', String(page));
      }
      return this.createPageUrl(params.toString());
    }

    getPaginationLabels() {
      const escapeHtml = (value) =>
        String(value || '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');

      const previousLabel =
        this.root.dataset.paginationPreviousLabel?.trim() || 'Previous';
      const nextLabel =
        this.root.dataset.paginationNextLabel?.trim() || 'Next';

      return {
        previousLabel,
        nextLabel,
        previousLabelHtml: escapeHtml(previousLabel),
        nextLabelHtml: escapeHtml(nextLabel),
      };
    }

    renderClientPagination(
      gridContent,
      searchParams,
      currentPage,
      totalPages
    ) {
      let pagination = gridContent.querySelector(
        '[data-collection-pagination]'
      );

      if (totalPages <= 1) {
        pagination?.remove();
        return;
      }

      if (!pagination) {
        pagination = gridContent.ownerDocument.createElement('div');
        pagination.className = 'mt-20 lg:mt-40';
        pagination.setAttribute('data-collection-pagination', '');
        gridContent.append(pagination);
      }

      const { previousLabelHtml, nextLabelHtml } = this.getPaginationLabels();

      const pageItems = this.getPaginationParts(currentPage, totalPages)
        .map((page) => {
          if (page === null) {
            return `<li><span class="ug-collection-new__pagination-item ug-collection-new__pagination-ellipsis" aria-hidden="true">…</span></li>`;
          }
          if (page === currentPage) {
            return `<li><span class="ug-collection-new__pagination-item is-current" aria-current="page">${page}</span></li>`;
          }
          return `<li><a href="${this.createPaginationHref(
            searchParams,
            page
          )}" class="ug-collection-new__pagination-item" aria-label="Page ${page}">${page}</a></li>`;
        })
        .join('');
      const prevItem =
        currentPage > 1
          ? `<li><a href="${this.createPaginationHref(
              searchParams,
              currentPage - 1
            )}" class="ug-collection-new__pagination-item ug-collection-new__pagination-prev" aria-label="${previousLabelHtml}">${previousLabelHtml}</a></li>`
          : '';
      const nextItem =
        currentPage < totalPages
          ? `<li><a href="${this.createPaginationHref(
              searchParams,
              currentPage + 1
            )}" class="ug-collection-new__pagination-item ug-collection-new__pagination-next" aria-label="${nextLabelHtml}">${nextLabelHtml}</a></li>`
          : '';

      pagination.innerHTML = `
        <nav class="ug-collection-new__pagination" role="navigation" aria-label="Pagination">
          <ul class="ug-collection-new__pagination-list gap-20 text-14 lg:gap-40 lg:text-14" role="list">
            ${prevItem}${pageItems}${nextItem}
          </ul>
        </nav>
      `;
    }

    prepareClientFilteredResponse(
      poolIndex,
      searchParams,
      { priceState = null, searchQuery = '' } = {}
    ) {
      const { firstDocument, firstRoot, groups, records } = poolIndex;
      const gridContent = firstRoot?.querySelector(
        '[data-collection-grid-content]'
      );
      const productGrid = gridContent?.querySelector(
        '[data-collection-product-grid]'
      );

      if (!gridContent || !productGrid) {
        return {
          html: poolIndex.htmlPages[0],
          searchParams,
          clientFiltered: true,
          records: [],
          groups,
          selections: new Map(),
        };
      }

      const minCents = priceState ? priceState.minValue * 100 : null;
      const maxCents = priceState ? priceState.maxValue * 100 : null;
      const normalizedQuery = searchQuery.trim().toLocaleLowerCase();
      const selections = this.getActiveFacetSelections(searchParams, groups);
      const priceSearchRecords = records.filter((record) => {
          if (priceState) {
            if (
              !Number.isFinite(record.priceCents) ||
              record.priceCents < minCents ||
              record.priceCents > maxCents
            ) {
              return false;
            }
          }

          if (normalizedQuery && !record.title.includes(normalizedQuery)) {
            return false;
          }

          return true;
        });
      const matchingRecords = priceSearchRecords.filter((record) =>
        this.recordMatchesSelections(record, groups, selections)
      );

      this.syncFacetAvailability(
        priceSearchRecords,
        firstRoot,
        groups,
        selections
      );

      const productsPerPage = this.getProductsPerPage(gridContent);
      const totalPages = Math.ceil(matchingRecords.length / productsPerPage);
      const requestedPage = Math.max(
        1,
        Number(new URLSearchParams(searchParams).get('page')) || 1
      );
      const currentPage =
        totalPages > 0 ? Math.min(requestedPage, totalPages) : 1;
      const startIndex = (currentPage - 1) * productsPerPage;
      const pageRecords = matchingRecords.slice(
        startIndex,
        startIndex + productsPerPage
      );
      const resolvedParams = new URLSearchParams(searchParams || '');
      this.stripLegacyPriceParams(resolvedParams);

      if (priceState) {
        resolvedParams.set(CLIENT_PRICE_MIN_PARAM, String(priceState.minValue));
        resolvedParams.set(CLIENT_PRICE_MAX_PARAM, String(priceState.maxValue));
      } else {
        resolvedParams.delete(CLIENT_PRICE_MIN_PARAM);
        resolvedParams.delete(CLIENT_PRICE_MAX_PARAM);
      }

      if (searchQuery) {
        resolvedParams.set(CLIENT_SEARCH_PARAM, searchQuery);
      } else {
        resolvedParams.delete(CLIENT_SEARCH_PARAM);
      }

      if (currentPage <= 1) {
        resolvedParams.delete('page');
      } else {
        resolvedParams.set('page', String(currentPage));
      }

      productGrid.replaceChildren(
        ...pageRecords.map((record) => record.card.cloneNode(true))
      );
      gridContent.dataset.totalPages = String(totalPages);
      gridContent.dataset.currentPage = String(currentPage);
      this.renderClientPagination(
        gridContent,
        resolvedParams.toString(),
        currentPage,
        totalPages
      );

      const emptyState = gridContent.querySelector(
        '[data-collection-search-empty]'
      );
      if (emptyState) {
        const showEmpty = matchingRecords.length === 0;
        emptyState.classList.toggle('hidden', !showEmpty);
        emptyState.classList.toggle('flex', showEmpty);
      }

      return {
        html: firstDocument.documentElement.outerHTML,
        searchParams: resolvedParams.toString(),
        clientFiltered: true,
        records: priceSearchRecords,
        groups,
        selections,
      };
    }

    async render(
      searchParams,
      {
        updateHistory = true,
        scrollToSection = false,
        focusId = null,
        updateFilters = false,
      } = {}
    ) {
      if (!this.sectionId || !this.gridContainer) return;

      const requestId = ++this.requestSequence;
      this.abortController?.abort();
      const controller = new AbortController();
      this.abortController = controller;
      this.setLoading(true);
      this.root.removeAttribute('data-collection-error');

      try {
        const normalizedParams = this.migrateLegacyClientParams(searchParams);
        const priceState = this.capturePriceStateFromParams(normalizedParams);
        const searchQuery = this.getClientSearchQuery(normalizedParams);
        const useClientFilter = this.needsClientFilterPool(normalizedParams, {
          priceState,
          searchQuery,
        });
        let resolvedSearchParams = normalizedParams;
        let clientFiltered = false;
        let countContext = null;
        let html;

        if (useClientFilter) {
          try {
            const poolParams = this.createProductPoolParams(normalizedParams);
            const poolIndex = await this.loadProductPool(poolParams.toString());
            if (requestId !== this.requestSequence) return;
            this.currentPoolIndex = poolIndex;

            const preparedResponse = this.prepareClientFilteredResponse(
              poolIndex,
              normalizedParams,
              { priceState, searchQuery }
            );
            html = preparedResponse.html;
            resolvedSearchParams = preparedResponse.searchParams;
            clientFiltered = preparedResponse.clientFiltered;
            countContext = {
              records: preparedResponse.records,
              groups: preparedResponse.groups,
              selections: preparedResponse.selections,
            };
          } catch (poolError) {
            if (poolError.name === 'AbortError') throw poolError;
            // 完整结果池拉取失败时回退普通 section，保证首屏/分页仍可渲染
            console.warn(
              '[UG collection] Product pool failed; falling back to section fetch.',
              poolError
            );
            html = await this.fetchSection(
              normalizedParams,
              controller.signal
            );
          }
        } else {
          html = await this.fetchSection(
            normalizedParams,
            controller.signal
          );
        }

        if (requestId !== this.requestSequence) return;

        this.renderResponse(html, focusId, {
          updateFilters,
          searchParams: resolvedSearchParams,
          clientFiltered,
          countContext,
        });
        this.root.removeAttribute('data-client-filter-pending');

        if (updateHistory) {
          this.updateHistory(resolvedSearchParams);
        }

        if (scrollToSection) {
          const behavior = window.matchMedia(
            '(prefers-reduced-motion: reduce)'
          ).matches
            ? 'auto'
            : 'smooth';

          this.root.scrollIntoView({
            behavior,
            block: 'start',
          });
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        if (requestId !== this.requestSequence) return;

        console.error('[UG collection] Failed to update products.', error);
        this.root.setAttribute('data-collection-error', 'true');
        this.root.dispatchEvent(
          new CustomEvent('ug:collection:error', {
            bubbles: true,
            detail: { error, searchParams },
          })
        );
      } finally {
        if (requestId === this.requestSequence) {
          this.setLoading(false);
          this.scheduleCardRowHeights();
          if (document.fonts?.ready) {
            document.fonts.ready.then(() => this.equalizeCardRowHeights());
          }
        }
      }
    }

    renderResponse(
      html,
      focusId,
      {
        updateFilters = false,
        searchParams = '',
        clientFiltered = false,
        countContext = null,
      } = {}
    ) {
      // 侧栏/排序层在 AJAX 重绘前关闭，避免 overflow-hidden 残留导致页面无法滚动。
      this.closeFilterDrawer({ revert: false, restoreFocus: false });
      this.closeSortSheet({ restoreFocus: false });
      this.closeSortMenu();

      const documentFromResponse = new DOMParser().parseFromString(
        html,
        'text/html'
      );
      const nextRoot =
        documentFromResponse.getElementById(this.root.id) ||
        documentFromResponse.querySelector(ROOT_SELECTOR);
      const nextGrid = nextRoot?.querySelector('#ProductGridContainer');
      const nextGridContent = nextGrid?.querySelector(
        '[data-collection-grid-content]'
      );

      if (!nextGrid || !nextGridContent || !this.gridContent) {
        throw new Error('The updated product grid was not returned.');
      }

      this.gridContent.innerHTML = nextGridContent.innerHTML;
      this.gridContent.dataset.totalPages =
        nextGridContent.dataset.totalPages || '1';
      this.gridContent.dataset.currentPage =
        nextGridContent.dataset.currentPage || '1';
      this.gridContent.dataset.productsPerPage =
        nextGridContent.dataset.productsPerPage ||
        this.gridContent.dataset.productsPerPage ||
        '1';
      this.gridContent.dataset.productsPerPageMobile =
        nextGridContent.dataset.productsPerPageMobile ||
        this.gridContent.dataset.productsPerPageMobile ||
        this.gridContent.dataset.productsPerPage ||
        '1';

      if (updateFilters) {
        const currentFilters = this.root.querySelector(
          '#main-collection-filters'
        );
        const nextFilters = nextRoot.querySelector(
          '#main-collection-filters'
        );

        if (currentFilters && nextFilters) {
          const openFilterIds = [
            ...currentFilters.querySelectorAll('.js-filter[open]'),
          ].map((element) => element.id);
          const savedPrice = this.capturePriceStateFromParams(searchParams);

          currentFilters.innerHTML = nextFilters.innerHTML;
          this.initializePriceRanges();
          if (savedPrice) this.restorePriceState(savedPrice);
          this.initializeFilterAccordions();

          openFilterIds.forEach((id) => {
            if (!id) return;
            const details = currentFilters.querySelector(
              `#${CSS.escape(id)}`
            );
            if (details) details.setAttribute('open', '');
          });
        }
      }

      const currentSort = this.root.querySelector('#SortBy');
      const nextSort = nextRoot.querySelector('#SortBy');
      if (currentSort && nextSort) {
        currentSort.value = nextSort.value;
        this.updateSortDisplay(nextSort.value);
      }

      this.gridContainer = this.root.querySelector('#ProductGridContainer');
      this.gridContent = this.gridContainer?.querySelector(
        '[data-collection-grid-content]'
      );

      const searchInput = this.root.querySelector('[data-collection-search]');
      const resolvedSearch = this.getClientSearchQuery(searchParams);
      if (searchInput && resolvedSearch !== searchInput.value) {
        searchInput.value = resolvedSearch;
      }
      this.syncSearchClearButton();

      if (clientFiltered) {
        this.syncClientEmptyState();
        if (countContext) {
          this.restoreFacetSelections(this.root, countContext.selections);
          this.syncFacetAvailability(
            countContext.records,
            this.root,
            countContext.groups,
            countContext.selections
          );
        }
      } else {
        // 仅价格滑块拖动预览会走当前页临时过滤；关键词搜索已改为完整结果池。
        this.applyClientFilters();
      }
      this.restoreFocus(focusId);

      if (
        typeof window.initializeScrollAnimationTrigger === 'function'
      ) {
        window.initializeScrollAnimationTrigger();
      }

      this.root.dispatchEvent(
        new CustomEvent('ug:collection:updated', {
          bubbles: true,
          detail: {
            searchParams,
          },
        })
      );
    }

    scheduleCardRowHeights() {
      // skeleton 隐藏网格时 getBoundingClientRect 为 0，需等显示后再量
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.equalizeCardRowHeights());
      });
    }

    syncClientEmptyState() {
      const cards = this.root.querySelectorAll('[data-collection-product]');
      const emptyState = this.root.querySelector(
        '[data-collection-search-empty]'
      );
      if (!emptyState) return;

      const showEmpty = cards.length === 0;
      emptyState.classList.toggle('hidden', !showEmpty);
      emptyState.classList.toggle('flex', showEmpty);
    }

    setLoading(isLoading) {
      this.root.toggleAttribute('data-loading', isLoading);
      this.gridContainer?.classList.toggle('is-skeleton', isLoading);
      this.gridContainer?.setAttribute(
        'aria-busy',
        isLoading ? 'true' : 'false'
      );

      const skeleton = this.gridContainer?.querySelector(
        '[data-collection-skeleton]'
      );
      if (skeleton) {
        skeleton.hidden = !isLoading;
        skeleton.setAttribute('aria-hidden', 'true');
      }

      this.root
        .querySelectorAll(
          '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
        )
        .forEach((spinner) => {
          spinner.classList.toggle('hidden', !isLoading);
        });
    }

    updateHistory(searchParams) {
      const url = this.createPageUrl(searchParams);
      window.history.pushState({ searchParams }, '', url);
    }

    createSectionUrl(searchParams) {
      const params = new URLSearchParams(searchParams);
      params.set('section_id', this.sectionId);
      return `${window.location.pathname}?${params.toString()}`;
    }

    createPageUrl(searchParams) {
      return `${window.location.pathname}${
        searchParams ? `?${searchParams}` : ''
      }`;
    }

    restoreFocus(focusId) {
      if (!focusId) return;

      const filter = [...this.root.querySelectorAll('.js-filter')].find(
        (element) => element.id === focusId
      );
      const focusTarget = filter?.querySelector(
        '.facets__summary, .mobile-facets__close-button, .ug-cf-summary'
      );
      focusTarget?.focus();
    }

    initializePriceRanges() {
      this.root
        .querySelectorAll('[data-ug-price-range-v2]')
        .forEach((priceRange) => {
          this.updatePriceRangeButtonLabels(priceRange);
          this.updatePriceRange(priceRange);
        });
    }

    initializeFilterAccordions() {
      this.filterAccordionAbortController?.abort();
      this.filterAccordionAbortController = new AbortController();
      const { signal } = this.filterAccordionAbortController;

      if (
        typeof CSS !== 'undefined' &&
        CSS.supports('interpolate-size', 'allow-keywords')
      ) {
        return;
      }

      const prefersReducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;
      const transition = prefersReducedMotion
        ? ''
        : 'height 0.35s cubic-bezier(0.4, 0, 0.2, 1)';

      this.root
        .querySelectorAll(
          '.ug-collection-filters-pc .ug-cf-group, .ug-cf-mobile-drawer .ug-cf-group'
        )
        .forEach((details) => {
          const summary = details.querySelector('.ug-cf-summary');
          const panel = details.querySelector('.ug-cf-panel');
          if (!summary || !panel || summary.dataset.ugCfAccordionBound) return;

          summary.dataset.ugCfAccordionBound = 'true';

          summary.addEventListener(
            'click',
            (event) => {
              event.preventDefault();

              if (details.open) {
                this.collapseFilterPanel(details, panel, transition);
              } else {
                this.expandFilterPanel(details, panel, transition);
              }
            },
            { signal }
          );
        });
    }

    expandFilterPanel(details, panel, transition) {
      details.setAttribute('open', '');

      if (!transition) return;

      panel.style.overflow = 'hidden';
      panel.style.height = '0px';
      panel.style.transition = transition;

      window.requestAnimationFrame(() => {
        panel.style.height = `${panel.scrollHeight}px`;
      });

      panel.addEventListener(
        'transitionend',
        (event) => {
          if (event.propertyName !== 'height') return;
          panel.style.height = 'auto';
          panel.style.overflow = '';
          panel.style.transition = '';
        },
        { once: true }
      );
    }

    collapseFilterPanel(details, panel, transition) {
      if (!transition) {
        details.removeAttribute('open');
        return;
      }

      panel.style.overflow = 'hidden';
      panel.style.height = `${panel.scrollHeight}px`;
      panel.style.transition = transition;

      window.requestAnimationFrame(() => {
        panel.style.height = '0px';
      });

      panel.addEventListener(
        'transitionend',
        (event) => {
          if (event.propertyName !== 'height') return;
          details.removeAttribute('open');
          panel.style.height = '';
          panel.style.overflow = '';
          panel.style.transition = '';
        },
        { once: true }
      );
    }

    initializeSortControls() {
      this.sortControlsAbortController?.abort();
      this.sortControlsAbortController = new AbortController();
      const { signal } = this.sortControlsAbortController;

      document.addEventListener('keydown', this.handleSortKeydown, { signal });

      const sortInput = this.root.querySelector('#SortBy');
      if (sortInput?.value) {
        this.updateSortDisplay(sortInput.value);
      }
    }

    updateSortDisplay(value) {
      let label = '';

      this.root.querySelectorAll('[data-sort-option]').forEach((option) => {
        const isActive = option.dataset.sortValue === value;
        option.classList.toggle('is-active', isActive);
        option.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        if (isActive) {
          label = option.textContent.trim();
        }
      });

      if (!label) return;

      this.root.querySelectorAll('[data-collection-sort-current]').forEach((element) => {
        element.textContent = label;
      });
    }

    applySortValue(value, labelText = '') {
      const sortForm = this.root.querySelector('#FacetSortForm');
      const sortInput = this.root.querySelector('#SortBy');
      if (!sortForm || !sortInput || !value || sortInput.value === value) {
        this.closeSortMenu();
        this.closeSortSheet();
        return;
      }

      sortInput.value = value;

      if (labelText) {
        this.root.querySelectorAll('[data-collection-sort-current]').forEach((element) => {
          element.textContent = labelText.trim();
        });
      }

      this.updateSortDisplay(value);
      this.closeSortMenu();
      this.closeSortSheet();
      this.submitForm(sortForm);
    }

    closeSortMenu() {
      this.root.querySelectorAll('.ug-collection-new__sort-dropdown.is-open').forEach((dropdown) => {
        dropdown.classList.remove('is-open');
        dropdown
          .querySelector('[data-collection-sort-trigger]')
          ?.setAttribute('aria-expanded', 'false');
        dropdown
          .querySelector('[data-collection-sort-menu]')
          ?.classList.remove('is-open');
      });
    }

    syncDocumentScrollLock() {
      const drawerOpen = this.root
        .querySelector('[data-collection-filter-drawer]')
        ?.classList.contains('is-open');
      const sheetOpen = this.root
        .querySelector('[data-collection-sort-sheet]')
        ?.classList.contains('is-open');

      document.documentElement.classList.toggle(
        'overflow-hidden',
        Boolean(drawerOpen || sheetOpen)
      );
    }

    openSortSheet(trigger) {
      if (window.matchMedia('(min-width: 1024px)').matches) return;

      const sheet = this.root.querySelector('[data-collection-sort-sheet]');
      if (!sheet) return;

      this.closeSortMenu();
      this.sortSheetTrigger = trigger || document.activeElement;
      sheet.style.display = 'block';
      sheet.getBoundingClientRect();
      window.requestAnimationFrame(() => {
        sheet.classList.add('is-open');
        this.syncDocumentScrollLock();
        this.getFocusableElements(sheet)[0]?.focus();
      });
      trigger?.setAttribute('aria-expanded', 'true');
    }

    closeSortSheet({ restoreFocus = true } = {}) {
      const sheet = this.root.querySelector('[data-collection-sort-sheet]');
      if (!sheet) return;

      const wasOpen = sheet.classList.contains('is-open');
      sheet.classList.remove('is-open');
      this.root
        .querySelector('[data-collection-sort-mobile-open]')
        ?.setAttribute('aria-expanded', 'false');
      this.syncDocumentScrollLock();

      window.setTimeout(() => {
        if (!sheet.classList.contains('is-open')) {
          sheet.style.removeProperty('display');
        }
      }, 350);

      if (wasOpen && restoreFocus) {
        this.sortSheetTrigger?.focus?.();
      }
    }

    openFilterDrawer() {
      if (window.matchMedia('(min-width: 1024px)').matches) return;

      const drawer = this.root.querySelector('[data-collection-filter-drawer]');
      if (!drawer) return;

      const desktopForm = this.root.querySelector('#FacetFiltersForm');
      if (desktopForm) {
        this.syncFilterForms(desktopForm);
      }
      this.updateMobileDraftFacetCounts();

      this.closeSortMenu();
      this.closeSortSheet({ restoreFocus: false });
      this.filterDrawerTrigger = document.activeElement;
      drawer.setAttribute('aria-hidden', 'false');
      window.clearTimeout(this.filterDrawerSettledTimeout);
      drawer.classList.remove('is-settled');
      window.requestAnimationFrame(() => {
        drawer.classList.add('is-open');
        this.syncDocumentScrollLock();
        this.getFocusableElements(drawer)[0]?.focus();
        this.filterDrawerSettledTimeout = window.setTimeout(() => {
          if (drawer.classList.contains('is-open')) {
            drawer.classList.add('is-settled');
          }
        }, 360);
      });
    }

    closeFilterDrawer({ revert = true, restoreFocus = true } = {}) {
      const drawer = this.root.querySelector('[data-collection-filter-drawer]');
      if (!drawer) return;

      const wasOpen = drawer.classList.contains('is-open');
      if (revert) {
        const desktopForm = this.root.querySelector('#FacetFiltersForm');
        if (desktopForm) {
          this.syncFilterForms(desktopForm);
        }
      }

      window.clearTimeout(this.filterDrawerSettledTimeout);
      drawer.classList.remove('is-settled');
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      this.syncDocumentScrollLock();
      if (wasOpen && restoreFocus) {
        this.filterDrawerTrigger?.focus?.();
      }
    }

    getPriceRangeState(priceRange) {
      const minInput = priceRange.querySelector('[data-price-min-input]');
      const maxInput = priceRange.querySelector('[data-price-max-input]');
      const minSlider = priceRange.querySelector('[data-price-min-slider]');
      const maxSlider = priceRange.querySelector('[data-price-max-slider]');

      const sliderMin = Number(
        priceRange.dataset.sliderMin || minSlider?.min || 0
      );
      const sliderMax = Number(
        priceRange.dataset.sliderMax || maxSlider?.max || 0
      );

      const min = Number(
        minInput?.value || minSlider?.value || sliderMin
      );
      const max = Number(
        maxInput?.value || maxSlider?.value || sliderMax
      );

      return { min, max, sliderMin, sliderMax };
    }

    presetMatchesState(button, state) {
      const buttonMin = Number(button.dataset.rangeMin || 0);
      const buttonMax = Number(
        button.dataset.rangeMax || state.sliderMax
      );

      return state.min === buttonMin && state.max === buttonMax;
    }

    syncActivePriceRangeButton(priceRange) {
      const state = this.getPriceRangeState(priceRange);

      priceRange
        .querySelectorAll('[data-price-range-button]')
        .forEach((button) => {
          button.classList.toggle(
            'is-active',
            this.presetMatchesState(button, state)
          );
        });
    }

    formatPriceLabel(priceRange, value) {
      const numericValue = Number(value);
      if (Number.isNaN(numericValue)) return '';

      const currency = priceRange.dataset.currency || '$';
      const currencyCode = priceRange.dataset.currencyCode || '';
      const currencyPosition =
        priceRange.dataset.currencyPosition || 'auto';
      const thousandsSeparator =
        priceRange.dataset.thousandsSeparator || 'auto';
      const locale =
        priceRange.dataset.locale ||
        document.documentElement.lang ||
        navigator.language;
      const numberOptions = {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      };
      const configuredGroupSeparator =
        {
          comma: ',',
          dot: '.',
          space: '\u00A0',
        }[thousandsSeparator] || '';
      const formatParts = (parts) =>
        parts
          .map((part) => {
            if (part.type === 'group' && configuredGroupSeparator) {
              return configuredGroupSeparator;
            }
            if (part.type === 'literal') {
              return part.value.replace(/\s/g, '');
            }
            return part.value;
          })
          .join('');
      const formatted = configuredGroupSeparator
        ? Math.round(numericValue)
            .toString()
            .replace(/\B(?=(\d{3})+(?!\d))/g, configuredGroupSeparator)
        : formatParts(
            new Intl.NumberFormat(locale, numberOptions).formatToParts(
              numericValue
            )
          );

      if (currencyPosition === 'before') {
        return `${currency}${formatted}`;
      }
      if (currencyPosition === 'after') {
        return `${formatted}${currency}`;
      }
      if (currencyCode) {
        const currencyParts = new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: currencyCode,
          currencyDisplay: 'narrowSymbol',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).formatToParts(numericValue);

        if (configuredGroupSeparator) {
          const currencyIndex = currencyParts.findIndex(
            (part) => part.type === 'currency'
          );
          const integerIndex = currencyParts.findIndex(
            (part) => part.type === 'integer'
          );
          const localizedCurrency =
            currencyParts[currencyIndex]?.value || currency;
          return currencyIndex < integerIndex
            ? `${localizedCurrency}${formatted}`
            : `${formatted}${localizedCurrency}`;
        }

        return formatParts(currencyParts);
      }

      return `${currency}${formatted}`;
    }

    updateDualPriceFill(priceRange) {
      const minSlider = priceRange.querySelector('[data-price-min-slider]');
      const maxSlider = priceRange.querySelector('[data-price-max-slider]');
      const fill = priceRange.querySelector('[data-dual-price-fill]');
      if (!minSlider || !maxSlider || !fill) return;

      const min = Number(minSlider.min || 0);
      const max = Number(maxSlider.max || 0);
      const range = max - min || 1;
      const minValue = Number(minSlider.value);
      const maxValue = Number(maxSlider.value);
      const start = ((Math.min(minValue, maxValue) - min) / range) * 100;
      const end = ((Math.max(minValue, maxValue) - min) / range) * 100;

      fill.style.left = `${start}%`;
      fill.style.right = `${100 - end}%`;
    }

    updatePriceLabels(priceRange) {
      const minLabel = priceRange.querySelector(
        '[data-price-slider-min-label]'
      );
      const maxLabel = priceRange.querySelector(
        '[data-price-slider-max-label]'
      );
      const { minValue, maxValue } = this.getPriceBoundValues(priceRange);

      if (minLabel) {
        minLabel.textContent = this.formatPriceLabel(priceRange, minValue);
      }

      if (maxLabel) {
        maxLabel.textContent = this.formatPriceLabel(priceRange, maxValue);
      }
    }

    updatePriceRangeButtonLabels(priceRange) {
      priceRange
        .querySelectorAll('[data-price-range-button]')
        .forEach((button) => {
          const minValue = Number(button.dataset.rangeMin);
          const maxValue = Number(button.dataset.rangeMax);
          if (Number.isNaN(minValue)) return;

          const minLabel = this.formatPriceLabel(priceRange, minValue);
          if (button.dataset.rangeOpenEnded === 'true') {
            button.textContent = `${minLabel}+`;
            return;
          }

          if (!Number.isNaN(maxValue)) {
            button.textContent = `${minLabel}-${this.formatPriceLabel(
              priceRange,
              maxValue
            )}`;
          }
        });
    }

    updatePriceRange(priceRange, changedInput = null) {
      const minimumInput =
        priceRange.querySelector('[data-price-min-input]') ||
        priceRange.querySelectorAll('input[type="number"]')[0];
      const maximumInput =
        priceRange.querySelector('[data-price-max-input]') ||
        priceRange.querySelectorAll('input[type="number"]')[1];
      const minSlider = priceRange.querySelector('[data-price-min-slider]');
      const maxSlider = priceRange.querySelector('[data-price-max-slider]');
      const slider = priceRange.querySelector('[data-price-slider]');

      if (!minimumInput || !maximumInput) return;

      if (minSlider && maxSlider) {
        const boundsMin = Math.round(Number(minSlider.min || 0));
        const boundsMax = Math.round(Number(maxSlider.max || 0));

        if (changedInput === minSlider || changedInput === maxSlider) {
          let minValue = Math.round(Number(minSlider.value));
          let maxValue = Math.round(Number(maxSlider.value));

          minValue = Math.min(Math.max(minValue, boundsMin), boundsMax);
          maxValue = Math.min(Math.max(maxValue, boundsMin), boundsMax);

          if (minValue > maxValue) {
            if (changedInput === minSlider) {
              maxValue = minValue;
            } else {
              minValue = maxValue;
            }
          }

          minSlider.value = String(minValue);
          maxSlider.value = String(maxValue);
          minimumInput.value = String(minValue);
          maximumInput.value = String(maxValue);
        } else {
          const minValue = Math.round(
            Number(minimumInput.value || minSlider.value || boundsMin)
          );
          const maxValue = Math.round(
            Number(maximumInput.value || maxSlider.value || boundsMax)
          );

          minSlider.value = String(
            Math.min(Math.max(minValue, boundsMin), boundsMax)
          );
          maxSlider.value = String(
            Math.min(Math.max(maxValue, boundsMin), boundsMax)
          );
          minimumInput.value = minSlider.value;
          maximumInput.value = maxSlider.value;
        }

        this.updateDualPriceFill(priceRange);
        this.updatePriceLabels(priceRange);
        this.syncActivePriceRangeButton(priceRange);
        return;
      }

      if (changedInput?.value) {
        const value = Number(changedInput.value);
        const minimum = Number(changedInput.getAttribute('min') || 0);
        const maximum = Number(
          changedInput.getAttribute('max') || Number.MAX_SAFE_INTEGER
        );
        changedInput.value = Math.min(
          Math.max(value, minimum),
          maximum
        );
      }

      if (slider && changedInput === slider && minimumInput) {
        minimumInput.value = slider.value;
      }

      if (slider && changedInput === minimumInput) {
        slider.value = minimumInput.value || slider.min;
      }

      maximumInput.setAttribute(
        'min',
        minimumInput.value || '0'
      );

      if (maximumInput.value) {
        minimumInput.setAttribute('max', maximumInput.value);
      }

      this.syncActivePriceRangeButton(priceRange);
    }

    clearClientSearch() {
      const input = this.root.querySelector('[data-collection-search]');
      if (!input) return;

      input.value = '';
      this.syncSearchClearButton();
      this.submitSearchDebounced.cancel();
      this.submitClientSearch();
      input.focus();
    }

    getActivePriceFilter() {
      const priceRange = this.root.querySelector('[data-ug-price-range-v2]');
      if (!priceRange) return null;

      const { sliderMin, sliderMax, minValue, maxValue } =
        this.getPriceBoundValues(priceRange);
      const isFullRange = minValue <= sliderMin && maxValue >= sliderMax;

      if (isFullRange) return null;

      return {
        minCents: minValue * 100,
        maxCents: maxValue * 100,
      };
    }

    isDesktopBreakpoint() {
      return window.matchMedia('(min-width: 1024px)').matches;
    }

    getProductsPerPage(gridContent = this.gridContent) {
      const desktopPerPage = Math.max(
        1,
        Number(gridContent?.dataset.productsPerPage) || 1
      );
      const mobilePerPage = Math.max(
        1,
        Number(gridContent?.dataset.productsPerPageMobile) || desktopPerPage
      );

      return this.isDesktopBreakpoint() ? desktopPerPage : mobilePerPage;
    }

    hasSplitPageSize(gridContent = this.gridContent) {
      const desktopPerPage = Math.max(
        1,
        Number(gridContent?.dataset.productsPerPage) || 1
      );
      const mobilePerPage = Math.max(
        1,
        Number(gridContent?.dataset.productsPerPageMobile) || desktopPerPage
      );

      return desktopPerPage !== mobilePerPage;
    }

    handleBreakpointChange() {
      if (this.hasSplitPageSize()) {
        const params = new URLSearchParams(
          this.migrateLegacyClientParams(window.location.search.slice(1))
        );
        params.delete('page');

        this.render(params.toString(), {
          updateHistory: true,
          updateFilters: false,
          scrollToSection: false,
        });
        return;
      }

      this.equalizeCardRowHeights();
    }

    getCardGridColumnCount() {
      return this.isDesktopBreakpoint() ? 3 : 2;
    }

    equalizeCardRowHeights() {
      const grid = this.root.querySelector('[data-collection-product-grid]');
      if (!grid) return;

      const cards = [...grid.querySelectorAll('.ug-collection-new__card')].filter(
        (card) => !card.hidden && !card.classList.contains('is-filtered-out')
      );
      const alignSelectors = [
        '[data-card-align="title"]',
        '[data-card-align="price-row"]',
      ];

      alignSelectors.forEach((selector) => {
        cards.forEach((card) => {
          const el = card.querySelector(selector);
          if (el) el.style.minHeight = '';
        });
      });

      if (cards.length === 0) return;

      // 先强制重排，再量自然高度，避免读到上一轮 minHeight
      void grid.offsetHeight;

      const columns = this.getCardGridColumnCount();

      for (let i = 0; i < cards.length; i += columns) {
        const rowCards = cards.slice(i, i + columns);

        alignSelectors.forEach((selector) => {
          let maxHeight = 0;

          rowCards.forEach((card) => {
            const el = card.querySelector(selector);
            if (!el) return;
            maxHeight = Math.max(maxHeight, el.getBoundingClientRect().height);
          });

          if (maxHeight <= 0) return;

          rowCards.forEach((card) => {
            const el = card.querySelector(selector);
            if (!el) return;
            el.style.minHeight = `${Math.ceil(maxHeight)}px`;
          });
        });
      }
    }

    applyClientFilters({ includePrice = false } = {}) {
      // 仅用于价格滑块拖动时的当前页预览；最终结果由完整结果池重分页。
      if (!includePrice) {
        this.syncSearchClearButton();
        return;
      }

      const priceFilter = this.getActivePriceFilter();
      const cards = [
        ...this.root.querySelectorAll('[data-collection-product]'),
      ];
      const emptyState = this.root.querySelector(
        '[data-collection-search-empty]'
      );
      const pagination = this.root.querySelector(
        '[data-collection-pagination]'
      );
      let visibleCount = 0;

      cards.forEach((card) => {
        let matchesPrice = true;
        if (priceFilter) {
          const priceCents = Number(card.dataset.priceCents);
          if (!Number.isNaN(priceCents)) {
            matchesPrice =
              priceCents >= priceFilter.minCents &&
              priceCents <= priceFilter.maxCents;
          }
        }

        card.hidden = !matchesPrice;
        card.classList.toggle('is-filtered-out', !matchesPrice);
        card.classList.toggle('hidden', !matchesPrice);
        if (matchesPrice) visibleCount += 1;
      });

      if (emptyState) {
        const showEmpty = cards.length > 0 && visibleCount === 0;
        emptyState.classList.toggle('hidden', !showEmpty);
        emptyState.classList.toggle('flex', showEmpty);
      }

      if (pagination) {
        pagination.hidden = Boolean(priceFilter);
      }
    }
  }

  const initialize = (scope = document) => {
    const roots = [];

    if (scope.matches?.(ROOT_SELECTOR)) {
      roots.push(scope);
    }

    roots.push(...scope.querySelectorAll(ROOT_SELECTOR));

    roots.forEach((root) => {
      if (root.ugMainCollectionNew) return;
      root.ugMainCollectionNew = new UgMainCollectionNew(root);
    });
  };

  initialize();

  document.addEventListener('shopify:section:load', (event) => {
    initialize(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.(ROOT_SELECTOR)
      ? event.target
      : event.target.querySelector(ROOT_SELECTOR);

    root?.ugMainCollectionNew?.destroy();
    if (root) delete root.ugMainCollectionNew;
  });
})();
